import { useCallback, useState, type DragEvent } from 'react'

/**
 * Dragging a row to a new position, over an intent that carries the
 * whole order — and, where a type offers one, into a different region
 * over an intent that carries both ends.
 *
 * **The mapping is exact, and that is why this is a hook rather than a
 * library.** Every `arrange` intent in the model takes the complete
 * order — a reorder is a rearrangement, and one that omits a placement
 * *keeps* it — so a drop within one list is one array, one intent and
 * one revision. The up/down arrows this replaces sent the same command
 * with two clicks per position and no picture of where a block was
 * going.
 *
 * **A drop into another region is a different command, and it has to
 * be.** A reorder names one region and refuses a link id that is not
 * already in it, so a drag from a page's header into its body cannot be
 * expressed as one — it is a `move`, which carries the region left, the
 * region joined and the position, and the fold does both halves in one
 * revision. Where a type declares no move intent, the drag simply does
 * not cross, because the alternative is a remove and a place: two
 * revisions with the block off the page in between, and for an inline
 * block a client sending back a copy of content it did not compute.
 *
 * **One hook per tree, not one per list.** A cross-region drop can only
 * be seen by something that watches both ends, and a hook per region
 * watches one. Each row names the *group* it belongs to — a routing
 * level, or one node's region — and carries whatever the caller needs to
 * act on it back out again, so nothing here keeps a lookup table keyed
 * by a string it built.
 *
 * The native HTML drag events rather than a drag-and-drop package. What
 * is needed is rows that reorder within themselves and occasionally move
 * between two lists on one screen, which is the case the platform
 * already does — and the alternative is a dependency in a client whose
 * whole argument is that its controls are derived from the wire rather
 * than assembled from parts.
 */
export interface DragRow {
  draggable: true
  onDragStart: (event: DragEvent) => void
  onDragEnd: () => void
  onDragOver: (event: DragEvent) => void
  onDragLeave: () => void
  onDrop: (event: DragEvent) => void
}

/** One end of a drag: which list, and whatever the caller needs to act on it. */
export interface End<M> {
  group: string
  meta: M
}

/** A drop that crossed into another list. */
export interface Move<M> {
  from: End<M>
  to: End<M>
  /** The row that moved. */
  id: string
  /** Where it landed in the destination, counted after it left the source. */
  position: number
}

interface Held<M> extends End<M> {
  id: string
  /** The stored order it was dragged out of, which says whether it is travelling up or down. */
  siblings: string[]
}

/**
 * Moving between lists, where the caller has an intent for it.
 *
 * Both halves are required together: a control that let a row be dropped
 * somewhere it cannot go would draw a landing line over an operation
 * that then silently does nothing.
 */
export interface Across<M> {
  /** Whether this row may leave `from` and join `to`. */
  allow: (from: End<M>, to: End<M>) => boolean
  send: (move: Move<M>) => void
}

export interface Reorder<M> {
  /**
   * Handlers for one row: the list it belongs to, its own id, that
   * list's stored order, and whatever the caller wants handed back.
   */
  rowProps: (group: string, id: string, siblings: string[], meta?: M) => DragRow
  /**
   * Handlers for a list's own container, so an empty one can still be
   * dropped into. A region with no rows has nothing to aim at, and it is
   * exactly the region an author is most likely to be filling.
   */
  zoneProps: (group: string, siblings: string[], meta?: M) => Omit<DragRow, 'draggable' | 'onDragStart' | 'onDragEnd'>
  /** Whether this row is the one being dragged, so it can be dimmed. */
  isHeld: (group: string, id: string) => boolean
  /** Whether the held row would land on this one, and from which side. */
  landingOn: (group: string, id: string) => 'above' | 'below' | undefined
  /** Whether the held row would land in this list, with no row under the pointer. */
  landingIn: (group: string) => boolean
}

/**
 * @param commit sends the arrangement for a drop inside one list — the
 * group that changed, and its whole new order.
 * @param enabled false while an intent is in flight, so a second drop
 * cannot be started against an order that is about to change.
 * @param across what to do with a drop that crossed lists, and whether
 * to allow one at all. Omitted, a drag never leaves the list it started
 * in.
 */
export function useReorder<M = undefined>(
  commit: (group: string, order: string[], meta?: M) => void,
  enabled = true,
  across?: Across<M>,
): Reorder<M> {
  const [held, setHeld] = useState<Held<M> | null>(null)
  const [over, setOver] = useState<{ group: string; id?: string } | null>(null)

  const clear = useCallback(() => {
    setHeld(null)
    setOver(null)
  }, [])

  // Whether the held row may land in this list at all. The same question
  // for the drag-over that draws the line and for the drop that sends
  // the command, so it is asked once here and neither can disagree.
  const accepts = useCallback(
    (group: string, meta?: M): boolean => {
      if (!held) return false
      if (held.group === group) return true
      if (!across) return false
      return across.allow(held, { group, meta: meta as M })
    },
    [across, held],
  )

  const land = useCallback(
    (group: string, meta: M | undefined, siblings: string[], target?: string) => {
      const dragged = held
      clear()
      if (!dragged || !accepts(group, meta)) return

      if (dragged.group === group) {
        // Inside one list: the whole order, which is what the intent
        // takes and what the author can see.
        const order = target === undefined ? undefined : moved(siblings, dragged.id, target)
        if (order) commit(group, order, meta)
        return
      }

      // Across two: the destination's own list decides the index, and it
      // is counted *without* the row that is arriving — which is what
      // the fold does too, so the number means the same at both ends.
      const at = target === undefined ? siblings.length : Math.max(siblings.indexOf(target), 0)
      across?.send({ from: dragged, to: { group, meta: meta as M }, id: dragged.id, position: at })
    },
    [accepts, across, clear, commit, held],
  )

  const rowProps = useCallback(
    (group: string, id: string, siblings: string[], meta?: M): DragRow => ({
      draggable: true,
      onDragStart: (event) => {
        if (!enabled) {
          event.preventDefault()
          return
        }
        // Firefox starts no drag at all without data on the transfer,
        // and the value is never read: the held row is state, because a
        // string cannot say which list it came from.
        event.dataTransfer.setData('text/plain', id)
        event.dataTransfer.effectAllowed = 'move'
        setHeld({ group, id, siblings, meta: meta as M })
      },
      onDragEnd: clear,
      onDragOver: (event) => {
        if (!held || (held.group === group && held.id === id)) return
        if (!accepts(group, meta)) return
        // Preventing the default is what marks a row as a drop target;
        // without it the browser refuses the drop and the cursor says
        // so.
        event.preventDefault()
        event.stopPropagation()
        event.dataTransfer.dropEffect = 'move'
        setOver({ group, id })
      },
      onDragLeave: () => setOver((current) => (current?.id === id && current.group === group ? null : current)),
      onDrop: (event) => {
        event.preventDefault()
        event.stopPropagation()
        land(group, meta, siblings, id)
      },
    }),
    [accepts, clear, enabled, held, land],
  )

  const zoneProps = useCallback(
    (group: string, siblings: string[], meta?: M) => ({
      onDragOver: (event: DragEvent) => {
        if (!accepts(group, meta)) return
        event.preventDefault()
        event.dataTransfer.dropEffect = 'move'
        setOver({ group })
      },
      onDragLeave: () => setOver((current) => (current?.group === group && current.id === undefined ? null : current)),
      onDrop: (event: DragEvent) => {
        event.preventDefault()
        land(group, meta, siblings)
      },
    }),
    [accepts, land],
  )

  return {
    rowProps,
    zoneProps,
    isHeld: (group, id) => held?.group === group && held.id === id,
    // Which edge of the row the line is drawn on, and it agrees with
    // where the row will actually go: after the target travelling down
    // its own list, before it travelling up, and before it when arriving
    // from somewhere else.
    landingOn: (group, id) => {
      if (!held || !over || over.group !== group || over.id !== id) return undefined
      if (held.group !== group) return 'above'
      const from = held.siblings.indexOf(held.id)
      const to = held.siblings.indexOf(id)
      if (from < 0 || to < 0) return undefined
      return from < to ? 'below' : 'above'
    },
    landingIn: (group) => Boolean(held && over?.group === group && over.id === undefined),
  }
}

/**
 * The order after a drop inside one list: the held entry removed, and
 * put back beside the row it landed on — after it when travelling down
 * the list, before it when travelling up.
 *
 * Undefined when the drop changes nothing, so a drag that ends where it
 * started sends no intent and writes no revision.
 */
export function moved(siblings: string[], held: string, target: string): string[] | undefined {
  const from = siblings.indexOf(held)
  const to = siblings.indexOf(target)
  if (from < 0 || to < 0 || from === to) return undefined
  const rest = siblings.filter((entry) => entry !== held)
  const at = rest.indexOf(target) + (from < to ? 1 : 0)
  const order = [...rest]
  order.splice(at, 0, held)
  return order
}
